const tokenize = (value) => (value || '').trim().split(/\s+/).filter(Boolean);

/**
 * Parse a single "Full Name" input into its parts.
 *
 * - "Last, First Middle" → surname is everything before the first comma.
 * - "First Middle Last"  → first token is the given name, last token is the
 *   surname and anything in between is the middle name.
 *
 * Middle names are never folded into the surname.
 */
export const parseFullName = (fullName) => {
  const value = (fullName || '').trim();
  const commaIndex = value.indexOf(',');

  if (commaIndex !== -1) {
    const surname = tokenize(value.slice(0, commaIndex)).join(' ');
    const [givenName = '', ...middle] = tokenize(value.slice(commaIndex + 1).replace(/,/g, ' '));
    return { givenName, middleName: middle.join(' '), surname };
  }

  const tokens = tokenize(value);
  if (tokens.length < 2) {
    return { givenName: tokens[0] || '', middleName: '', surname: '' };
  }

  return {
    givenName: tokens[0],
    middleName: tokens.slice(1, -1).join(' '),
    surname: tokens[tokens.length - 1],
  };
};

export const composeFullName = ({ givenName, middleName, surname } = {}) => [
  givenName,
  middleName,
  surname,
].map((part) => (part || '').trim()).filter(Boolean).join(' ');

/**
 * Resolve the name parts to submit. When the field still holds the prefilled
 * name, the original parts are returned as is so a load → save round trip
 * never re-parses (and reshuffles) them.
 */
export const resolveNameParts = (fullName, prefilledParts = null) => {
  if (prefilledParts && composeFullName(prefilledParts) === (fullName || '').trim()) {
    const { givenName = '', middleName = '', surname = '' } = prefilledParts;
    return { givenName, middleName: middleName || '', surname };
  }
  return parseFullName(fullName);
};
